import { useContext, useCallback, useEffect } from 'react';

import { ViewerContext } from '@vimeo/viewer';

import { CacheContext } from './CacheContext';
import { MaybeArray, ResourceDeclaration, buildQueryString, buildAcceptHeader } from './libs';
import { useFetchReducer, State } from './useFetchReducer';

type QueryOptions<Variables> = {
  variables?: Variables;
  resourceKey?: string;
  ignoreCache?: boolean;
  accept?: MaybeArray<ResourceDeclaration>;
  headerOverride?: {
    Authorization?: string;
    Accept?: string;
    'Content-type'?: string;
  };
};

function buildUrl<Variables>(url: string, ...configs: QueryOptions<Variables>[]) {
  let callUrl = url;

  const options = Object.assign({}, ...configs);
  const { variables } = options;

  if (variables) {
    callUrl += buildQueryString(variables);
  }

  const { accept, headerOverride } = options;
  let { resourceKey } = options;

  if (!resourceKey) {
    resourceKey = callUrl;
  }

  return { callUrl, resourceKey, accept, headerOverride };
}

/**
 * @deprecated Use endpoint-specific hooks from `@vimeo/gctl-api` instead.
 */
export function useLazyQuery<Data = unknown, Variables extends Record<string, unknown> = {}>(
  url: string,
  baseOptions?: QueryOptions<Variables>,
): [(options?: QueryOptions<Variables>) => Promise<void>, State<Data>] {
  const viewer = useContext(ViewerContext);
  const cache = useContext(CacheContext);

  if (!cache) {
    throw new Error(`useMutation must be used within a <CacheContext.Provider />'s scope`);
  }

  const [state, dispatch] = useFetchReducer<Data>();

  const handleQuery = useCallback(
    async (callsiteOptions?: QueryOptions<Variables>) => {
      if (!viewer) {
        return;
      }

      const { callUrl, resourceKey, accept, headerOverride } = buildUrl(
        url,
        baseOptions || {},
        callsiteOptions || {},
      );

      const cachedResource = cache.get(resourceKey);

      if (cachedResource && !callsiteOptions?.ignoreCache) {
        dispatch({ type: 'CACHE_HIT', payload: cachedResource });
        return;
      }

      dispatch({ type: 'REQUEST' });

      try {
        const headers = {
          Authorization: `jwt ${viewer.jwt}`,
          Accept: buildAcceptHeader(accept),
          'Content-Type': 'application/json',
          'Accept-Language': viewer.locale || 'en',
          ...(headerOverride || {}),
        };
        const response = await fetch(`https://${viewer.apiUrl}${callUrl}`, {
          method: 'GET',
          headers,
        });

        let data = await response.json();

        cache.set(resourceKey, data);

        dispatch({ type: 'SUCCESS', payload: data });
      } catch (err) {
        dispatch({ type: 'FAILURE', payload: err });
      }
    },
    [url, baseOptions, viewer, dispatch, cache],
  );

  const { resourceKey } = buildUrl(url, baseOptions || {});

  useEffect(() => {
    const unsubscribe = cache.subscribeToKey(resourceKey, (value) => {
      dispatch({ type: 'CACHE_HIT', payload: value });
    });

    return () => {
      unsubscribe();
    };
  }, [cache, resourceKey, dispatch]);

  return [handleQuery, state];
}
